//REV-22010 — the i18n keys for the AI pipeline's feedback on a DOCUMENT: reading state, reading
//quality, why a document was not interpreted, why no account was proposed.
//
//Product decision (taken by the user, REV-22010): the document page MAY say WHY the AI produced
//nothing — as a reason from a CLOSED list mapped to i18n keys, never a raw code, never an amount,
//never free text. Every map below is a TOTAL `Record` over its closed union (the
//`UPSTREAM_STATE_LABEL_KEYS` construction): a new member cannot compile without a decision about what
//the trustee reads for it, and an unknown string from the wire finds no key and renders nothing.
//
//One file for both surfaces (hub marker + document page) and for the Node guard
//(`src/tests/document-feedback-keys.unit.spec.ts`), which is why it imports no Angular: the guard
//loads it without Angular.

import { masterCovers } from '@modeso/aumico-lib-ai-agents-fe/interfaces/ai-org-settings.interface';
import type { DocumentReadingState } from '../types/closing-documents.types';
import type {
    DocumentInterpretationReason,
    DocumentProposalRefusal,
} from '../components/document-detail/document-detail.types';

/** Reading state → its label. Absent state (read OK) has no key — nothing is drawn. */
export const READING_STATE_LABEL_KEYS: Readonly<Record<DocumentReadingState, string>> = {
    pending: 'abcp_document_reading_state_pending',
    reading: 'abcp_document_reading_state_reading',
    deferredDailyCap: 'abcp_document_reading_state_deferred_daily_cap',
    failed: 'abcp_document_reading_state_failed',
    notSupported: 'abcp_document_reading_state_not_supported',
};

/**
 * The fixed keys of the document page's AI-reading block: its title, the "read" line, and the line
 * for a read that lost the document's structure.
 */
export const READING_BLOCK_KEYS = {
    title: 'abcp_document_reading_title',
    read: 'abcp_document_reading_read',
    structureFail: 'abcp_document_reading_quality_structure_fail',
} as const;

/** Why a document was not interpreted → the sentence under the empty-state label. */
export const INTERPRETATION_REASON_KEYS: Readonly<Record<DocumentInterpretationReason, string>> = {
    notEnabled: 'abcp_document_interpretation_reason_not_enabled',
    limitReached: 'abcp_document_interpretation_reason_limit_reached',
    closingHasNoBalanceSheetDate: 'abcp_document_interpretation_reason_closing_has_no_balance_sheet_date',
    readingUnusable: 'abcp_document_interpretation_reason_reading_unusable',
    modelAnswerUnusable: 'abcp_document_interpretation_reason_model_answer_unusable',
    kindNotSupported: 'abcp_document_interpretation_reason_kind_not_supported',
    noBookingOwner: 'abcp_document_interpretation_reason_no_booking_owner',
    statementDatesUnreadable: 'abcp_document_interpretation_reason_statement_dates_unreadable',
    statementEndsBeforeBalanceSheetDate: 'abcp_document_interpretation_reason_statement_ends_before_balance_sheet_date',
    quoteNotInDocument: 'abcp_document_interpretation_reason_quote_not_in_document',
    amountUnreadable: 'abcp_document_interpretation_reason_amount_unreadable',
};

/**
 * `disabled` + `limitReached` gets its OWN state label: the existing `disabled` label says the
 * interpretation is "switched off for this organisation", which is false for a spent budget — nobody
 * switched anything off, and the reason line under it would contradict the headline.
 */
export const INTERPRETATION_LIMIT_REACHED_LABEL_KEY = 'abcp_document_detail_interpretation_limit_reached';

/** Why no account was proposed → the notice in the picker state, under its own title. */
export const PROPOSAL_REFUSAL_KEYS: Readonly<Record<DocumentProposalRefusal, string>> = {
    noAccountForKind: 'abcp_document_proposal_refusal_no_account_for_kind',
    accountsNotClassified: 'abcp_document_proposal_refusal_accounts_not_classified',
    severalAccountsMatch: 'abcp_document_proposal_refusal_several_accounts_match',
};

export const PROPOSAL_REFUSAL_TITLE_KEY = 'abcp_document_proposal_refusal_title';

/**
 * The documents tab's banner when the AI document mapping (task type `ABCP_DOCUMENT_KIND_CLASSIFY`)
 * and/or the interpretation (`ABCP_DOCUMENT_NORMALIZATION`) may not run for the organisation — read from
 * the existing `GET /api/ai/org-settings/:orgId` by the HOST page (`closing-documents-tab.page.ts`),
 * which links to the AI settings. One sentence per combination, so no sentence is glued together.
 */
export const AI_DISABLED_BANNER_KEYS = {
    mapping: 'abcp_documents_ai_disabled_mapping',
    interpretation: 'abcp_documents_ai_disabled_interpretation',
    both: 'abcp_documents_ai_disabled_both',
    action: 'abcp_documents_ai_disabled_action',
    /** For a non-admin: the settings page is admin-only, so the banner names who can switch it on. */
    contactAdmin: 'abcp_documents_ai_disabled_contact_admin',
} as const;

/**
 * The two task types of the document pipeline in the AI service. Mirrors `DOCUMENT_KIND_TASK_TYPE` /
 * `NORMALIZATION_TASK_TYPE` in aumico-ms-ai-agents — the services share no code, only these wire
 * identifiers.
 */
export const DOCUMENT_MAPPING_TASK_TYPE = 'ABCP_DOCUMENT_KIND_CLASSIFY';
export const DOCUMENT_INTERPRETATION_TASK_TYPE = 'ABCP_DOCUMENT_NORMALIZATION';

/** The slice of `GET /api/ai/org-settings/:orgId` the banner reads. */
export interface AiSettingsForBanner {
    readonly aiFeaturesEnabled?: boolean;
    readonly aiFeaturesAcceptedVersion?: number | null;
    readonly masterAgb?: { readonly version: number };
    readonly agents?: readonly { readonly taskType: string; readonly enabled: boolean }[];
}

/**
 * The banner key for the org's AI settings, or `''` for "no banner". Reads the SAME rule the AI
 * service's gate applies (`checkAndEnforce`): a task runs when the general AI switch is on for the
 * current master AGB, or its own entry is `enabled: true`. `null`/`undefined` settings (not loaded,
 * or the read failed) ⇒ `''`: a banner must never claim a state nobody read.
 */
export function aiDisabledBannerKeyOf(settings: AiSettingsForBanner | null | undefined): string {
    if (settings == null) {
        return '';
    }
    const masterOn = masterCovers(settings);
    const enabled = (taskType: string): boolean =>
        masterOn || (settings.agents ?? []).some((agent) => agent.taskType === taskType && agent.enabled === true);
    const mappingOff = !enabled(DOCUMENT_MAPPING_TASK_TYPE);
    const interpretationOff = !enabled(DOCUMENT_INTERPRETATION_TASK_TYPE);
    if (mappingOff && interpretationOff) {
        return AI_DISABLED_BANNER_KEYS.both;
    }
    if (mappingOff) {
        return AI_DISABLED_BANNER_KEYS.mapping;
    }
    return interpretationOff ? AI_DISABLED_BANNER_KEYS.interpretation : '';
}

/**
 * May this user follow the AI-off banner to the AI settings? The SAME rule as that route's `AdminGuard`:
 * the `ORG_ADMIN` role in the token claims — `APP_ADMIN` alone is NOT enough, because the guard does not
 * let it through either, and a link that ends on a refusal is no way out. Anything unknown (no roles, a
 * failed read) is `false`: the page then shows the "ask your administrator" hint instead of a dead link.
 */
export function canOpenAiSettings(roles: readonly string[] | null | undefined): boolean {
    return (roles ?? []).includes('ORG_ADMIN');
}

/**
 * A key from a total map, or `''` for a value the map does not know (a wire string outside the closed
 * list). `''` renders nothing — never the raw value, which is the whole point of the closed list.
 */
export function feedbackKeyOf<K extends string>(map: Readonly<Record<K, string>>, value: string | null | undefined): string {
    return value != null && Object.prototype.hasOwnProperty.call(map, value) ? map[value as K] : '';
}

/** REV-22302 — a quote's line mark "p2·L40" → page and line; anything else is not shown. */
export function quoteLineOf(mark: string | null | undefined): { page: string; line: string } | null {
    const match = /^p(\d+)·L(\d+)$/.exec(mark ?? '');
    return match ? { page: match[1], line: match[2] } : null;
}

/** A quoted OCR line for display: without table pipes and markdown emphasis. */
export function quoteTextOf(quote: string | null | undefined): string {
    return (quote ?? '')
        .replace(/[|*_]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/** `2025-12-31` (or a full ISO instant) → `31.12.2025`; `—` for anything else. */
export function isoDateLabel(iso: string | null | undefined): string {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
    return match ? `${match[3]}.${match[2]}.${match[1]}` : '—';
}

/** Every key this file can produce — what the i18n guard checks against all five locale files. */
export const DOCUMENT_FEEDBACK_KEYS: readonly string[] = [
    ...Object.values(READING_STATE_LABEL_KEYS),
    ...Object.values(READING_BLOCK_KEYS),
    ...Object.values(INTERPRETATION_REASON_KEYS),
    INTERPRETATION_LIMIT_REACHED_LABEL_KEY,
    ...Object.values(PROPOSAL_REFUSAL_KEYS),
    PROPOSAL_REFUSAL_TITLE_KEY,
    ...Object.values(AI_DISABLED_BANNER_KEYS),
];
